import { useIndirectTaxRegistrations } from '@commenda/api/indirect-tax/registrations';
import { IndirectTaxJurisdictionType } from '@commenda/api/indirect-tax/registrations/types';
import { Button } from '@commenda/kakashi';
import { ServiceRequestModel } from '@commenda/utils';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { useSalesTaxFilingDetails } from '../../../sales-tax/filing/dashboard/useSalesTaxFilingDetail';
import { IndirectTaxEditRegistrationWizard } from '../../../indirect-tax/registrations/components/modals/IndirectTaxEditRegistrationWizard';
import { IndirectTaxRegistrationsHelper } from '../../../indirect-tax/registrations/helpers';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';

export const ProvideCredentialsTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate: refetchServiceRequest } = context;
  const { setNotification } = useNotification();

  const metadata = serviceRequest?.metadata as {
    dueDate: string;
    state: string;
  };

  const [isEditWizardOpen, setIsEditWizardOpen] = useState(false);
  const router = useRouter();

  const { data: salesTaxRegistrations } = useIndirectTaxRegistrations({
    filters: {
      corporationId: serviceRequest?.corporation?.id,
      states: [metadata?.state],
      countries: ['US'],
      jurisdictionTypes: [IndirectTaxJurisdictionType.STATE_OR_PROVINCE],
    },
  });

  const registrationData = useMemo(() => {
    const registration = salesTaxRegistrations?.find(
      (reg) => reg.state === metadata?.state
    );
    return registration
      ? IndirectTaxRegistrationsHelper.enrichWithSettings(registration)
      : null;
  }, [salesTaxRegistrations, metadata?.state]);

  const { filing, refetchFiling } = useSalesTaxFilingDetails({
    filingId: serviceRequest?.salesTaxFilingId,
    corporationId: serviceRequest?.corporationId,
  });

  const viewRegistrationForApproval = () => {
    if (serviceRequest && serviceRequest.corporation) {
      if (serviceRequest.salesTaxNewRegistration?.salesTaxRegistrationId) {
        router.push(
          ServiceRequestModel.getCompanyRegistrationLink(
            serviceRequest.salesTaxNewRegistration.salesTaxRegistrationId,
            serviceRequest.corporationId
          )
        );
      } else {
        router.push(
          `/clients/${serviceRequest.corporation.id}/indirect-tax/registrations`
        );
      }
    }
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditWizardOpen(true)}
              >
                Start
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Provide tax portal credential" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="State registration review in progress"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditWizardOpen(true)}
              >
                View
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Credentials validated"
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            label: (
              <ServiceRequestTaskLabel label="Provide tax portal credential" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={viewRegistrationForApproval}
              >
                View Registration
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Complete state registration review"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditWizardOpen(true)}
              >
                View
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Credentials validated"
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      {registrationData && (
        <IndirectTaxEditRegistrationWizard
          isOpen={isEditWizardOpen}
          onClose={() => setIsEditWizardOpen(false)}
          corporationId={serviceRequest.corporation.id}
          registrationData={registrationData}
          setNotification={setNotification}
          onSuccess={() => {
            refetchFiling();
            refetchServiceRequest();
          }}
        />
      )}
    </div>
  );
};
